import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["searchInput", "photoGrid", "photoItem", "loadingIndicator", "scrollTrigger", "loadMoreButton", "toolbarShell"]
  static values = {
    nextPage: Number,
    loading: Boolean,
    hasMore: Boolean,
    eventId: String,
    feedSeed: Number,
    localOnly: Boolean,
    nativeApp: Boolean,
    initialLoad: Boolean,
    toolbarCompactThreshold: { type: Number, default: 40 }
  }
  
  initialize() {
    this.currentSearchQuery = ''
  }

  connect() {
    this.updateToolbarState = this.updateToolbarState.bind(this)
    this.toolbarTicking = false
    this.toolbarManuallyExpanded = false
    this.toolbarExpandedAtScrollY = 0
    window.addEventListener('scroll', this.updateToolbarState, { passive: true })
    this.updateToolbarState()

    if (this.hasSearchInputTarget) {
      this.currentSearchQuery = this.searchInputTarget.value.trim()
    }

    // Only initialize masonry if photoGrid exists (not needed for simple scrolling sections)
    if (this.hasPhotoGridTarget) {
      this.initializeMasonryLayout()
      this.initializeExistingPhotos()
      if (this.initialLoadValue) {
        this.loadInitialPhotos()
      } else {
        this.setupInfiniteScroll()
      }
    }
  }

  disconnect() {
    window.removeEventListener('scroll', this.updateToolbarState)
    window.cancelAnimationFrame(this.toolbarFrame)
    this.toolbarTicking = false

    if (this.scrollHandler) {
      window.removeEventListener('scroll', this.scrollHandler)
    }
    if (this.observer) {
      this.observer.disconnect()
    }
    if (this.resizeHandler) {
      window.removeEventListener('resize', this.resizeHandler)
    }
  }

  updateToolbarState() {
    if (!this.hasToolbarShellTarget || this.toolbarTicking) return

    this.toolbarTicking = true
    this.toolbarFrame = window.requestAnimationFrame(() => {
      if (
        this.toolbarManuallyExpanded &&
        Math.abs(window.scrollY - this.toolbarExpandedAtScrollY) > 8
      ) {
        this.toolbarManuallyExpanded = false
      }

      this.toolbarShellTarget.classList.toggle(
        'is-compact',
        !this.toolbarManuallyExpanded && window.scrollY >= this.toolbarCompactThresholdValue
      )
      this.toolbarTicking = false
    })
  }

  expandToolbar() {
    if (!this.hasToolbarShellTarget || !this.toolbarShellTarget.classList.contains('is-compact')) return

    this.toolbarManuallyExpanded = true
    this.toolbarExpandedAtScrollY = window.scrollY
    this.toolbarShellTarget.classList.remove('is-compact')
  }

  search() {
    this.performSearch()
  }

  toggleLocalOnly(event) {
    const localOnly = event.currentTarget.value === "1"
    this.localOnlyValue = localOnly
    this.performSearch()
  }

  async performSearch() {
    const searchQuery = this.searchInputTarget.value.trim()
    
    this.currentSearchQuery = searchQuery
    this.loadingValue = true
    this.showLoadingIndicator()
    
    try {
      // Determine the URL based on current page
      const currentPath = window.location.pathname
      let url = currentPath === '/' ? '/' : currentPath
      const params = []
      if (searchQuery) params.push(`q=${encodeURIComponent(searchQuery)}`)
      if (!searchQuery && this.hasFeedSeedValue) params.push(`feed_seed=${this.feedSeedValue}`)
      if (this.hasEventIdValue && this.eventIdValue) params.push(`event_id=${this.eventIdValue}`)
      if (this.localOnlyValue) params.push(`local_only=1`)
      if (params.length > 0) url += `?${params.join('&')}`

      if (!this.hasPhotoGridTarget) {
        window.location.href = url
        return
      }
      
      const response = await fetch(url, {
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }
      })
      
      if (!response.ok) throw new Error('Search failed')
      
      const data = await response.json()
      
      // Clear existing photos
      this.photoGridTarget.innerHTML = ''
      
      // Add new photos
      const tempDiv = document.createElement('div')
      tempDiv.innerHTML = data.photos
      const newPhotos = Array.from(tempDiv.querySelectorAll('.photo-pin'))

      this.nextPageValue = data.next_page
      this.hasMoreValue = data.has_more
      if (data.feed_seed) this.feedSeedValue = data.feed_seed

      if (newPhotos.length === 0) {
        this.showEmptySearchState(data, searchQuery)
        return
      }
      
      newPhotos.forEach(photo => {
        this.photoGridTarget.appendChild(photo)
      })
      
      // Reinitialize masonry
      this.initializeMasonryLayout()
      this.initializeExistingPhotos()
      
    } catch (error) {
      console.error('Search error:', error)
      // Could show error message to user here
    } finally {
      this.loadingValue = false
      this.hideLoadingIndicator()
    }
  }

  initializeMasonryLayout() {
    if (this.resizeHandler) {
      window.removeEventListener('resize', this.resizeHandler)
    }

    this.updateColumnWidth()

    this.resizeHandler = this.throttle(() => {
      this.updateColumnWidth()
      this.layoutMasonry()
    }, 250)

    window.addEventListener('resize', this.resizeHandler)
  }
  
  updateColumnWidth() {
    const containerWidth = this.photoGridTarget.offsetWidth
    const columns = this.getColumnCount()
    const gutter = this.getGutter()
    this.columnWidth = Math.floor((containerWidth - (gutter * (columns - 1))) / columns)
  }
  
  getColumnWidth() {
    const containerWidth = this.photoGridTarget.offsetWidth
    const columns = this.getColumnCount()
    const gutter = this.getGutter()
    return Math.floor((containerWidth - (gutter * (columns - 1))) / columns)
  }
  
  getColumnCount() {
    const width = window.innerWidth
    if (width < 640) return 2 // Mobile
    if (width < 768) return 3 // Small tablet  
    if (width < 1024) return 4 // Large tablet
    return 4 // Desktop
  }

  getGutter() {
    return window.innerWidth < 640 ? 16 : 28
  }

  layoutMasonry() {
    if (!this.hasPhotoGridTarget) return

    const photos = this.photoItemTargets
    const columns = this.getColumnCount()
    const gutter = this.getGutter()
    const gridWidth = (this.columnWidth * columns) + (gutter * (columns - 1))
    const columnHeights = Array(columns).fill(0)

    this.photoGridTarget.style.width = `${gridWidth}px`

    photos.forEach(photo => {
      photo.style.width = `${this.columnWidth}px`
      photo.style.position = 'absolute'

      const columnIndex = columnHeights.indexOf(Math.min(...columnHeights))
      photo.style.left = `${columnIndex * (this.columnWidth + gutter)}px`
      photo.style.top = `${columnHeights[columnIndex]}px`
      columnHeights[columnIndex] += photo.offsetHeight + gutter
    })

    this.photoGridTarget.style.height = `${Math.max(...columnHeights) - gutter}px`
  }

  scheduleMasonryLayout() {
    window.requestAnimationFrame(() => this.layoutMasonry())
  }
  
  initializeExistingPhotos() {
    const photos = this.photoItemTargets
    if (photos.length === 0) return

    photos.forEach(photo => {
      photo.style.width = `${this.columnWidth}px`
      photo.style.position = 'absolute'
      photo.style.opacity = '1'
      photo.style.visibility = 'visible'
      photo.classList.add('masonry-ready')
    })
    this.layoutMasonry()
  }
  
  appendNewPhotos(newPhotos) {
    newPhotos.forEach(photo => {
      photo.style.width = `${this.columnWidth}px`
      photo.style.position = 'absolute'
      photo.style.opacity = '1'
      photo.style.visibility = 'visible'
      photo.classList.add('fade-in')
    })
    this.layoutMasonry()
  }

  openModal(event) {
    const item = event.currentTarget

    // On Hotwire Native, navigate to the dedicated photo page — the path
    // configuration presents /photos/:share_token as a native bottom sheet.
    const shareToken = item.dataset.shareToken
    if (this.nativeAppValue && shareToken) {
      Turbo.visit(`/photos/${shareToken}`)
      return
    }

    const img = item.querySelector('img')
    const imageUrl = img.getAttribute('data-full-url') || img.src
    const caption = item.dataset.photoCaption || ''
    const description = item.dataset.photoDescription || ''
    const source = item.dataset.photoSource || ''
    const photoId = item.dataset.photoId
    const photoFavorited = item.dataset.photoFavorited
    const companyId = item.dataset.companyId
    const companyName = item.dataset.companyName
    const companyAvatar = item.dataset.companyAvatar
    const companyLogo = item.dataset.companyLogo
    const eventId = item.dataset.eventId
    const eventPermalink = item.dataset.eventPermalink
    const eventName = item.dataset.eventName
    const eventLogo = item.dataset.eventLogo
    const eventOrganization = item.dataset.eventOrganization
    const eventDates = item.dataset.eventDates
    const entryNumber = item.dataset.entryNumber
    const entryLabel = item.dataset.entryLabel
    const projectId = item.dataset.projectId
    const currentTourProject = item.dataset.currentTourProject
    const projectInTour = item.dataset.projectInTour
    const projectPermalink = item.dataset.projectPermalink
    const projectTitle = item.dataset.projectTitle
    const projectPhoto = item.dataset.projectPhoto
    const projectCity = item.dataset.projectCity
    const projectState = item.dataset.projectState
    const projectSquareFootage = item.dataset.projectSquareFootage
    const projectBedrooms = item.dataset.projectBedrooms
    const projectBathrooms = item.dataset.projectBathrooms
    
    // Parse contributions JSON if present
    let contributions = []
    if (item.dataset.contributions) {
      try {
        contributions = JSON.parse(item.dataset.contributions)
      } catch (error) {
        console.error('Error parsing contributions:', error);
      }
    }
    
    window.openPhotoModal(imageUrl, caption, description, source, {
      photoId,
      photoFavorited,
      companyId,
      companyName,
      companyAvatar,
      companyLogo,
      eventId,
      eventPermalink,
      eventName,
      eventLogo,
      eventOrganization,
      eventDates,
      entryNumber,
      entryLabel,
      shareToken,
      projectId,
      currentTourProject,
      projectInTour,
      projectPermalink,
      projectTitle,
      projectPhoto,
      projectCity,
      projectState,
      projectSquareFootage,
      projectBedrooms,
      projectBathrooms,
      contributions
    })
  }

  // Handle image loading errors
  imageError(event) {
    const img = event.target
    const placeholder = img.closest('.photo-pin')
    
    // Show placeholder or remove item
    placeholder.style.opacity = '0.5'
    img.alt = 'Image could not be loaded'
  }

  // Handle successful image load
  imageLoaded(event) {
    const item = event.target.closest('.photo-pin')
    item.classList.remove('loading')
    this.scheduleMasonryLayout()
  }
  
  // Toggle favorite status
  toggleFavorite(event) {
    event.preventDefault()
    event.stopPropagation() // Prevent modal from opening
    event.stopImmediatePropagation() // Ensure no other handlers fire
    
    const button = event.currentTarget
    const photoId = button.dataset.photoId
    const isFavorited = button.dataset.favorited === 'true'
    
    // Add pulse animation
    button.classList.add('favoriting')
    setTimeout(() => button.classList.remove('favoriting'), 300)
    
    // Optimistically update UI
    this.updateHeartIcon(button, !isFavorited)
    button.dataset.favorited = !isFavorited
    
    // Send AJAX request
    const url = `/favorites/photo/${photoId}`
    const method = isFavorited ? 'DELETE' : 'POST'
    
    fetch(url, {
      method: method,
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content,
        'Accept': 'application/json'
      }
    })
    .then(response => {
      if (!response.ok) {
        console.error('Favorite request failed:', response.status, response.statusText)
      }
      return response.json()
    })
    .then(data => {
      if (data.status === 'guest') {
        this.updateHeartIcon(button, isFavorited)
        button.dataset.favorited = isFavorited
        window.dispatchEvent(new CustomEvent('guestAction'))
      } else if (data.status === 'error') {
        console.error('Favorite error:', data.message)
        // Revert on error
        this.updateHeartIcon(button, isFavorited)
        button.dataset.favorited = isFavorited
      }
    })
    .catch(error => {
      console.error('Favorite request exception:', error)
      // Revert on error
      this.updateHeartIcon(button, isFavorited)
      button.dataset.favorited = isFavorited
    })
  }
  
  updateHeartIcon(button, isFavorited) {
    const svg = button.querySelector('svg')
    const iconClass = button.dataset.iconClass || 'text-gray-700'
    const filledIconClass = button.dataset.filledIconClass || 'text-primary-600'
    const label = button.querySelector('[data-favorite-label]')
    
    if (isFavorited) {
      // Filled heart
      svg.innerHTML = `
        <path d="M12 21s-9-4.78-9-12c0-2.485 2.1-4.5 4.688-4.5 1.935 0 3.597 1.126 4.312 2.733C12.715 5.626 14.377 4.5 16.313 4.5 18.9 4.5 21 6.515 21 9c0 7.22-9 12-9 12z"/>
      `
      svg.setAttribute('fill', 'currentColor')
      svg.removeAttribute('stroke')
      svg.removeAttribute('stroke-width')
      svg.classList.remove('text-white', 'text-gray-700', 'text-primary-600', iconClass)
      svg.classList.add(filledIconClass)
      button.title = 'Saved Photo'
      if (label) label.textContent = 'Saved Photo'
    } else {
      // Empty heart
      svg.innerHTML = `
        <path stroke-linecap="round" stroke-linejoin="round" d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z"/>
      `
      svg.removeAttribute('fill')
      svg.setAttribute('stroke', 'currentColor')
      svg.setAttribute('stroke-width', '2')
      svg.classList.remove('text-white', 'text-gray-700', 'text-primary-600', filledIconClass)
      svg.classList.add(iconClass)
      button.title = 'Save Photo'
      if (label) label.textContent = 'Save Photo'
    }
  }

  isMobile() {
    return window.matchMedia('(max-width: 767px)').matches
  }

  // Infinite scroll setup
  setupInfiniteScroll() {
    if (this.isMobile()) {
      if (this.hasMoreValue && this.hasLoadMoreButtonTarget) {
        this.loadMoreButtonTarget.classList.remove('hidden')
      }
      return
    }

    // Method 1: Traditional scroll event (with throttling)
    this.scrollHandler = this.throttle(this.handleScroll.bind(this), 100)
    window.addEventListener('scroll', this.scrollHandler)

    // Method 2: Intersection Observer (more reliable)
    this.setupIntersectionObserver()
  }
  
  setupIntersectionObserver() {
    if ('IntersectionObserver' in window && this.hasScrollTriggerTarget) {
      this.observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            this.loadMorePhotos()
          }
        })
      }, {
        rootMargin: '300px 0px 0px 0px'
      })
      
      this.observer.observe(this.scrollTriggerTarget)
    }
  }

  // Simple throttle function
  throttle(func, delay) {
    let timeoutId
    let lastExecTime = 0
    return function (...args) {
      const currentTime = Date.now()
      
      if (currentTime - lastExecTime > delay) {
        func.apply(this, args)
        lastExecTime = currentTime
      } else {
        clearTimeout(timeoutId)
        timeoutId = setTimeout(() => {
          func.apply(this, args)
          lastExecTime = Date.now()
        }, delay - (currentTime - lastExecTime))
      }
    }
  }

  handleScroll() {
    // Don't load more if already loading or no more content
    const hasMore = this.hasMoreValue === true || this.hasMoreValue === 'true'
    const loading = this.loadingValue === true || this.loadingValue === 'true'
    
    if (loading || !hasMore) {
      return
    }

    // Check if user has scrolled near the bottom
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop
    const windowHeight = window.innerHeight
    const documentHeight = document.documentElement.scrollHeight
    
    if (scrollTop + windowHeight >= documentHeight - 300) {
      this.loadMorePhotos()
    }
  }


  async loadMore() {
    if (this.hasLoadMoreButtonTarget) {
      this.loadMoreButtonTarget.classList.add('hidden')
    }
    await this.loadMorePhotos()
    if (this.hasMoreValue && this.hasLoadMoreButtonTarget) {
      this.loadMoreButtonTarget.classList.remove('hidden')
    }
  }

  async loadInitialPhotos() {
    this.nextPageValue = 1
    this.hasMoreValue = true
    await this.loadMorePhotos()
    this.initialLoadValue = false
    this.setupInfiniteScroll()
  }

  async loadMorePhotos() {
    const hasMore = this.hasMoreValue === true || this.hasMoreValue === 'true'
    const loading = this.loadingValue === true || this.loadingValue === 'true'
    
    if (loading || !hasMore || !this.nextPageValue) {
      return
    }

    this.loadingValue = true
    this.showLoadingIndicator()

    try {
      // Build URL with search query and event_id if present
      const params = [`page=${this.nextPageValue}`]
      if (this.currentSearchQuery) {
        params.push(`q=${encodeURIComponent(this.currentSearchQuery)}`)
      }
      if (!this.currentSearchQuery && this.hasFeedSeedValue) {
        params.push(`feed_seed=${this.feedSeedValue}`)
      }
      if (this.hasEventIdValue && this.eventIdValue) {
        params.push(`event_id=${this.eventIdValue}`)
      }
      if (this.localOnlyValue) {
        params.push(`local_only=1`)
      }
      
      // Determine the URL based on current page
      const currentPath = window.location.pathname
      const baseUrl = currentPath === '/' ? '/' : currentPath
      const url = `${baseUrl}?${params.join('&')}`
      
      const response = await fetch(url, {
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }
      })

      if (!response.ok) throw new Error('Network response was not ok')
      
      const data = await response.json()
      
      // Create a temporary container to parse the HTML
      const tempDiv = document.createElement('div')
      tempDiv.innerHTML = data.photos
      
      // Get new photos and append them to DOM
      const newPhotos = Array.from(tempDiv.querySelectorAll('.photo-pin'))
      if (newPhotos.length === 0 && this.photoItemTargets.length === 0) {
        this.showEmptySearchState(data, this.currentSearchQuery)
      }
      newPhotos.forEach(photo => {
        this.photoGridTarget.appendChild(photo)
      })
      
      // Update pagination values
      this.nextPageValue = data.next_page
      this.hasMoreValue = data.has_more
      if (data.feed_seed) this.feedSeedValue = data.feed_seed
      
      this.appendNewPhotos(newPhotos)
      
    } catch (error) {
      // Silently handle error
    } finally {
      this.loadingValue = false
      this.hideLoadingIndicator()
    }
  }

  showLoadingIndicator() {
    if (this.hasLoadingIndicatorTarget) {
      this.loadingIndicatorTarget.classList.remove('hidden')
    }
  }

  hideLoadingIndicator() {
    if (this.hasLoadingIndicatorTarget) {
      this.loadingIndicatorTarget.classList.add('hidden')
    }
  }

  showEmptySearchState(data, searchQuery) {
    const vectorUnavailable = data.search_result_source === 'vector_unavailable'
    const title = vectorUnavailable ? 'Image search is temporarily unavailable' : (searchQuery ? 'No matching photos found' : 'No photos yet')
    const message = vectorUnavailable
      ? 'Visual matching could not run right now. Please try again shortly.'
      : (searchQuery ? `No photos matched "${this.escapeHTML(searchQuery)}".` : 'Check back soon for new inspiration.')

    this.photoGridTarget.style.width = '100%'
    this.photoGridTarget.style.height = 'auto'
    this.photoGridTarget.innerHTML = `
      <div class="w-full py-16 text-center">
        <svg class="h-20 w-20 text-slate-300 dark:text-slate-600 mx-auto mb-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
        <h3 class="text-xl font-semibold text-slate-800 dark:text-slate-200 mb-2">${title}</h3>
        <p class="text-slate-600 dark:text-slate-400">${message}</p>
      </div>
    `
  }

  escapeHTML(value) {
    return value.replace(/[&<>"']/g, character => ({
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#39;'
    }[character]))
  }
}
